import { create } from "zustand"

// Promise chains and version refs aren't reactive — kept in a module-level map.
// The store only holds the pending count (reactive, drives button disabling).
type QueueState = {
	promise: Promise<unknown>
	versionRef: { current: number }
}
const queues = new Map<string, QueueState>()

type Store = {
	pendingByKey: Record<string, number>
	inc: (key: string) => void
	dec: (key: string) => void
}

export const useMutationQueueStore = create<Store>((set) => ({
	pendingByKey: {},
	inc: (key) =>
		set((state) => ({
			pendingByKey: { ...state.pendingByKey, [key]: (state.pendingByKey[key] ?? 0) + 1 },
		})),
	dec: (key) =>
		set((state) => {
			const next = (state.pendingByKey[key] ?? 0) - 1
			return { pendingByKey: { ...state.pendingByKey, [key]: next < 0 ? 0 : next } }
		}),
}))

const ensureQueue = (key: string, initialVersion: number): QueueState => {
	const existing = queues.get(key)
	if (existing) {
		// Keep the version ref monotonic — only advance, never revert (a stale prop after a
		// successful mutation must not pull versionRef back).
		if (initialVersion > existing.versionRef.current) existing.versionRef.current = initialVersion
		return existing
	}
	const created: QueueState = { promise: Promise.resolve(), versionRef: { current: initialVersion } }
	queues.set(key, created)
	return created
}

export const enqueueMutation = <T>(key: string, initialVersion: number, task: () => Promise<T>): Promise<T> => {
	const entry = ensureQueue(key, initialVersion)
	const { inc, dec } = useMutationQueueStore.getState()
	inc(key)
	const next = entry.promise.then(task, task)
	entry.promise = next.catch(() => {})
	next.finally(() => dec(key))
	return next
}

export const getQueueVersionRef = (key: string, initialVersion: number) => ensureQueue(key, initialVersion).versionRef

// Test-only: wipe all queues and pending counts. Module-level state otherwise leaks
// across tests sharing the same entity id.
export const resetMutationQueues = () => {
	queues.clear()
	useMutationQueueStore.setState({ pendingByKey: {} })
}
